import React, { useState } from 'react';
import { TOPIC_IDEAS, SECTION_COLORS, TRIAL_FREE_TOPICS } from '../data/topicIdeas';
import { isTrial } from '../data/storage';


// ── colour helpers ────────────────────────────────────────────────
function sectionCfg(color) {
  return SECTION_COLORS[color] || SECTION_COLORS.teal;
}

const CHIP_COLORS = {
  purple: { bg:'#ede9fe', color:'#5b21b6', border:'#c4b5fd' },
  teal:   { bg:'#ccfbf1', color:'#0f766e', border:'#99f6e4' },
  green:  { bg:'#dcfce7', color:'#15803d', border:'#86efac' },
  blue:   { bg:'#dbeafe', color:'#1d4ed8', border:'#93c5fd' },
  orange: { bg:'#ffedd5', color:'#c2410c', border:'#fdba74' },
  red:    { bg:'#fee2e2', color:'#dc2626', border:'#fca5a5' },
};

// ── Idea cards (left panel in practice view) ──────────────────────
function IdeaSection({ section, checked, onToggle }) {
  const cfg = sectionCfg(section.color);
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: cfg.label, marginBottom: 7 }}>
        {section.label}
      </div>
      {section.items.map((item, i) => {
        const key = `${section.label}__${i}`;
        const isChecked = checked[key];
        return (
          <div key={key}
            onClick={() => onToggle(key)}
            style={{
              display: 'flex', gap: 10, alignItems: 'flex-start',
              background: isChecked ? cfg.bg : '#fafafa',
              border: `1px solid ${isChecked ? cfg.border : '#e8e8e8'}`,
              borderRadius: 8, padding: '9px 12px', marginBottom: 6,
              cursor: 'pointer', transition: 'all .15s',
            }}>
            <div style={{
              width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
              border: `2px solid ${isChecked ? cfg.label : '#d1d5db'}`,
              background: isChecked ? cfg.label : '#ffffff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {isChecked && <span style={{ color: '#fff', fontSize: 11, lineHeight: 1 }}>&#10003;</span>}
            </div>
            <div style={{ fontSize: 13, color: isChecked ? cfg.text : '#374151', lineHeight: 1.6 }}>
              {item}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Vocab chips ───────────────────────────────────────────────────
function VocabChips({ items, color = 'purple', onInsert }) {
  const cfg = CHIP_COLORS[color] || CHIP_COLORS.purple;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
      {items.map((word, i) => (
        <button key={i}
          onClick={() => onInsert(word)}
          title="Click to copy to your paragraph"
          style={{
            background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
            borderRadius: 100, padding: '3px 11px', fontSize: 12.5,
            cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif",
            transition: 'all .12s',
          }}>
          {word}
        </button>
      ))}
    </div>
  );
}

// ── Score badge ───────────────────────────────────────────────────
function ScoreBadge({ score }) {
  const map = {
    'Excellent':    { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    'Good':         { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
    'Developing':   { bg: '#fef3c7', color: '#b45309', border: '#fde68a' },
    'Needs Work':   { bg: '#fee2e2', color: '#dc2626', border: '#fca5a5' },
  };
  const cfg = map[score] || map['Developing'];
  return (
    <span style={{
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      borderRadius: 100, padding: '4px 14px',
      fontFamily: "'JetBrains Mono',monospace", fontSize: 11, fontWeight: 700,
      letterSpacing: '.06em',
    }}>
      {score}
    </span>
  );
}

// ── Practice screen for a selected topic ─────────────────────────
function PracticeScreen({ topicKey, data, candidate, onBack }) {
  const [writing, setWriting] = useState('');
  const [checked, setChecked] = useState({});
  const [feedback, setFeedback] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState('');

  const wc = writing.trim().split(/\s+/).filter(Boolean).length;

  const vocabSection = data.sections.find(s => s.color === 'purple');
  const ideaSections = data.sections.filter(s => s.color !== 'purple');
  const checkedIdeas = Object.entries(checked).filter(([, v]) => v).map(([k]) => {
    const [sectionLabel, idx] = k.split('__');
    const sec = data.sections.find(s => s.label === sectionLabel);
    return sec ? sec.items[parseInt(idx)] : null;
  }).filter(Boolean);

  function toggleIdea(key) {
    setChecked(c => ({ ...c, [key]: !c[key] }));
  }

  function insertVocab(word) {
    setWriting(w => w ? w + ' ' + word : word);
    setCopied(word);
    setTimeout(() => setCopied(''), 1500);
  }

  async function handleFeedback() {
    if (wc < 30)  { setError('Please write at least 30 words before requesting feedback.'); return; }
    setError('');
    setFeedback(null);
    setLoading(true);
    try {
      const ideasUsed = checkedIdeas.length
        ? checkedIdeas.map((idea, i) => `${i + 1}. ${idea}`).join('\n')
        : 'None ticked — check whether the student used ideas from the bank anyway.';
      const vocab = vocabSection ? vocabSection.items.join(', ') : '';

      const res = await fetch('/api/anthropic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: 'claude-sonnet-4-5',
          max_tokens: 700,
          system: 'You are an encouraging IELTS Writing coach assessing a student\'s practice paragraph. Be specific, warm, and focused. Always quote their exact words when praising or critiquing. Give one clear, actionable improvement. Return ONLY valid JSON.',
          messages: [{
            role: 'user',
            content: `Task: "${data.task}"\n\nIdeas the student planned to use:\n${ideasUsed}\n\nAvailable vocabulary: ${vocab}\n\nStudent paragraph:\n"${writing}"\n\nAssess across 4 criteria. Return ONLY valid JSON:\n{\n  "score": "Good",\n  "mainIdea": "Did they state a clear main point? Quote their words.",\n  "development": "Did they explain or develop it? What could be added?",\n  "example": "Did they give a specific example? Which one / what is missing?",\n  "vocabulary": "Did they use any of the provided vocabulary? Which words were strong?",\n  "tip": "One specific sentence they could add or change to improve this paragraph significantly."\n}\n\nScore must be one of: Excellent, Good, Developing, Needs Work.`,
          }],
        }),
      });
      const d = await res.json();
      const text = d.content?.[0]?.text || '{}';
      setFeedback(JSON.parse(text.replace(/```json|```/g, '').trim()));
    } catch (e) {
      setError('Could not get feedback: ' + e.message);
    }
    setLoading(false);
  }

  return (
    <div style={{ fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18 }}>
        <button onClick={onBack}
          style={{ background: '#f7f9fc', border: '1px solid #dde3ed', color: '#5a6a88', padding: '6px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
          ← Topics
        </button>
        <div>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: '#9aa3b8' }}>
            Ideas Practice
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2a4a' }}>{data.topic}</div>
        </div>
      </div>

      {/* Task instruction */}
      <div style={{ background: '#1a2a4a', borderRadius: 10, padding: '16px 18px', marginBottom: 20 }}>
        <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: '#b8860b', marginBottom: 7 }}>
          Your Task
        </div>
        <div style={{ fontSize: 14, color: '#ffffff', lineHeight: 1.7 }}>{data.task}</div>
        <div style={{ marginTop: 10, fontSize: 11.5, color: 'rgba(255,255,255,0.45)' }}>
          Tick the ideas you plan to use &#8594; Write your paragraph &#8594; Get AI feedback
        </div>
      </div>

      {/* Two-column layout */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>

        {/* LEFT: Idea bank */}
        <div style={{ flex: '1 1 300px', minWidth: 0 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>
            Idea Bank — tick points you plan to use
          </div>
          {ideaSections.map((sec, i) => (
            <IdeaSection key={i} section={sec} checked={checked} onToggle={toggleIdea} />
          ))}

          {vocabSection && (
            <div>
              <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: SECTION_COLORS.purple.label, marginBottom: 7 }}>
                Key Vocabulary — click to insert
              </div>
              <VocabChips items={vocabSection.items} color="purple" onInsert={insertVocab} />
              {copied && (
                <div style={{ fontSize: 11, color: '#7c3aed', marginTop: -6, marginBottom: 8 }}>
                  &#10003; "{copied}" inserted
                </div>
              )}
            </div>
          )}
        </div>

        {/* RIGHT: Writing area */}
        <div style={{ flex: '1 1 320px', minWidth: 0 }}>
          <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, letterSpacing: '.12em', textTransform: 'uppercase', color: '#5a6a88', marginBottom: 10 }}>
            Your Paragraph
          </div>
          <textarea
            value={writing}
            onChange={e => setWriting(e.target.value)}
            placeholder={'Write your paragraph here…\n\nAim for 80–120 words. Use at least ONE idea from the bank and try to include a specific example.'}
            rows={10}
            style={{
              width: '100%', background: '#ffffff', border: '1.5px solid #c0c0c0',
              borderRadius: 8, padding: '13px 14px', color: '#1a1a1a',
              fontSize: 14.5, lineHeight: 1.85, fontFamily: "'Times New Roman',serif",
              outline: 'none', resize: 'vertical', boxSizing: 'border-box',
              transition: 'border-color .2s',
            }}
          />
          {/* Word count bar */}
          <div style={{ background: '#f7f9fc', borderRadius: '0 0 8px 8px', padding: '7px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: -3, border: '1px solid #e8e8e8', borderTop: 'none' }}>
            <span style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: wc >= 80 ? '#15803d' : '#9aa3b8' }}>
              {wc} / 80–120 words {wc >= 80 && '&#10003;'}
            </span>
            {checkedIdeas.length > 0 && (
              <span style={{ fontSize: 11, color: '#7c3aed', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                &#10003; {checkedIdeas.length} idea{checkedIdeas.length > 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          {error && (
            <div style={{ marginTop: 10, padding: '9px 13px', background: '#fff5f5', border: '1px solid #fca5a5', borderRadius: 7, fontSize: 12.5, color: '#dc2626' }}>
              {error}
            </div>
          )}

          <button
            onClick={handleFeedback}
            disabled={loading}
            style={{
              marginTop: 12, width: '100%', background: loading ? '#6a8ab0' : '#1a2a4a',
              color: '#ffffff', border: 'none', padding: '12px', borderRadius: 8,
              fontSize: 14, fontWeight: 600, cursor: loading ? 'default' : 'pointer',
              fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'background .2s',
            }}>
            {loading ? '&#8987; Analysing your paragraph…' : '&#129302; Get AI Feedback'}
          </button>
        </div>
      </div>

      {/* Feedback panel */}
      {feedback && (
        <div style={{ background: '#ffffff', border: '1px solid #c8d0e0', borderRadius: 10, overflow: 'hidden', boxShadow: '0 2px 10px rgba(26,42,74,0.08)' }}>
          {/* Feedback header */}
          <div style={{ background: '#1a2a4a', padding: '13px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#ffffff' }}>AI Coach Feedback</div>
            {feedback.score && <ScoreBadge score={feedback.score}/>}
          </div>

          <div style={{ padding: '16px 18px' }}>
            {/* 4 criteria */}
            {[
              { label: '&#9679; Main Idea', value: feedback.mainIdea,    color: '#1d4ed8', bg: '#eff6ff' },
              { label: '&#9679; Development', value: feedback.development, color: '#7c3aed', bg: '#faf5ff' },
              { label: '&#9679; Example',   value: feedback.example,     color: '#0f766e', bg: '#f0fdfa' },
              { label: '&#9679; Vocabulary', value: feedback.vocabulary,  color: '#c2410c', bg: '#fff7ed' },
            ].map(({ label, value, color, bg }) => value && (
              <div key={label} style={{ background: bg, border: `1px solid ${color}22`, borderRadius: 8, padding: '10px 13px', marginBottom: 10 }}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, color, letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>
                  <span dangerouslySetInnerHTML={{ __html: label }} />
                </div>
                <div style={{ fontSize: 13, color: '#1a2233', lineHeight: 1.65 }}>{value}</div>
              </div>
            ))}

            {/* Tip */}
            {feedback.tip && (
              <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '11px 14px', marginTop: 6 }}>
                <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 9.5, color: '#b45309', letterSpacing: '.1em', textTransform: 'uppercase', marginBottom: 5 }}>
                  &#128161; One Key Improvement
                </div>
                <div style={{ fontSize: 13.5, color: '#78350f', lineHeight: 1.7, fontStyle: 'italic' }}>{feedback.tip}</div>
              </div>
            )}

            {/* Try again */}
            <button
              onClick={() => { setFeedback(null); setError(''); }}
              style={{ marginTop: 14, background: '#f7f9fc', border: '1px solid #dde3ed', color: '#5a6a88', padding: '8px 18px', borderRadius: 7, fontSize: 12.5, cursor: 'pointer' }}>
              Edit &amp; Try Again
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Topic selection grid ──────────────────────────────────────────
function TopicGrid({ trial, onSelect }) {
  const entries = Object.entries(TOPIC_IDEAS);
  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 700, color: '#1a2a4a', marginBottom: 6 }}>
          Ideas Practice
        </div>
        <div style={{ fontSize: 13, color: '#5a6a88', lineHeight: 1.65 }}>
          Choose a topic, read the idea bank, tick the points you plan to use, then write ONE body paragraph and get instant AI coaching feedback.
          {trial && (
            <span style={{ display: 'inline-block', marginLeft: 8, background: '#fff8f0', border: '1px solid #fde8cc', color: '#b8620a', borderRadius: 100, padding: '1px 10px', fontSize: 11.5, fontWeight: 600 }}>
              Free trial: 3 topics
            </span>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 12 }}>
        {entries.map(([key, data]) => {
          const locked = trial && !TRIAL_FREE_TOPICS.includes(key);
          const firstSection = data.sections[0];
          const cfg = sectionCfg(firstSection?.color || 'teal');
          return (
            <div key={key}
              onClick={() => !locked && onSelect(key)}
              style={{
                background: locked ? '#f9fafb' : '#ffffff',
                border: `1.5px solid ${locked ? '#e5e7eb' : cfg.border}`,
                borderRadius: 10, padding: '14px 16px',
                cursor: locked ? 'default' : 'pointer', opacity: locked ? 0.6 : 1,
                transition: 'all .18s',
                boxShadow: locked ? 'none' : '0 1px 4px rgba(26,42,74,0.06)',
              }}
              onMouseEnter={e => { if (!locked) { e.currentTarget.style.boxShadow = '0 4px 14px rgba(26,42,74,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = locked ? 'none' : '0 1px 4px rgba(26,42,74,0.06)'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 7 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: locked ? '#9ca3af' : '#1a2a4a', lineHeight: 1.3 }}>
                  {data.topic}
                </div>
                {locked
                  ? <span style={{ fontSize: 13, flexShrink: 0, marginLeft: 6 }}>&#128274;</span>
                  : <span style={{ fontSize: 9, fontFamily: "'JetBrains Mono',monospace", background: cfg.bg, color: cfg.label, border: `1px solid ${cfg.border}`, borderRadius: 4, padding: '2px 7px', flexShrink: 0, marginLeft: 6 }}>OPEN</span>
                }
              </div>
              <div style={{ fontSize: 11.5, color: locked ? '#d1d5db' : '#6b7280', lineHeight: 1.55 }}>
                {data.sections.length} idea categories &bull; {data.sections.reduce((t, s) => t + s.items.length, 0)} points
              </div>
              {!locked && (
                <div style={{ marginTop: 9, display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {data.sections.slice(0, 3).map((sec, i) => {
                    const scfg = sectionCfg(sec.color);
                    return (
                      <span key={i} style={{ fontSize: 10, background: scfg.bg, color: scfg.label, border: `1px solid ${scfg.border}`, borderRadius: 3, padding: '1px 6px' }}>
                        {sec.label.split(' ').slice(0, 2).join(' ')}
                      </span>
                    );
                  })}
                </div>
              )}
              {locked && trial && (
                <div style={{ marginTop: 7, fontSize: 11, color: '#9ca3af' }}>Standard / Premium only</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────
export default function IdeaPractice({ branding, candidate, tiers }) {
  const [selectedKey, setSelectedKey] = useState(null);
  const trial = isTrial(candidate);

  if (selectedKey && TOPIC_IDEAS[selectedKey]) {
    return (
      <PracticeScreen
        topicKey={selectedKey}
        data={TOPIC_IDEAS[selectedKey]}
        candidate={candidate}
        onBack={() => setSelectedKey(null)}
      />
    );
  }

  return <TopicGrid trial={trial} onSelect={setSelectedKey} />;
}
